/**
 * Agent 管理 API 客户端
 * 专门处理 AI Agents 的管理功能
 */

const API_BASE_AGENTS = '/api';

// ==================== 类型定义 ====================

export interface Agent {
  id?: number;
  tenant_code: string;
  agent_name: string;
  agent_id: string;
  agent_type: 'chatbot' | 'assistant' | 'workflow';
  description?: string;
  usage_type?: 'employee' | 'customer';
  default_operator_employee_id?: number;
  service_id?: number; // 关联的 AI Platform 服务 ID
  service_name?: string; // 服务名称
  service_url?: string; // 服务 URL
  platform_type?: string; // 平台类型
  app_id?: string; // 应用 ID
  model_name?: string;
  max_tokens?: number;
  temperature?: number;
  system_prompt?: string;
  processing_mode: 'sync' | 'async';
  timeout_seconds?: number;
  max_retries?: number;
  retry_delay_seconds?: number;
  is_active: boolean;
  last_used_at?: string;
  usage_count?: number;
  created_at?: string;
  updated_at?: string;
}

export interface CreateAgentRequest {
  tenant_code: string;
  agent_name: string;
  agent_id: string;
  agent_type?: 'chatbot' | 'assistant' | 'workflow';
  description?: string;
  usage_type?: 'employee' | 'customer';
  default_operator_employee_id?: number;
  service_id?: number; // 关联的 AI Platform 服务 ID
  model_name?: string;
  max_tokens?: number;
  temperature?: number;
  system_prompt?: string;
  processing_mode?: 'sync' | 'async';
  timeout_seconds?: number;
  max_retries?: number;
  retry_delay_seconds?: number;
  is_active?: boolean;
}

export interface UpdateAgentRequest {
  agent_name?: string;
  agent_type?: 'chatbot' | 'assistant' | 'workflow';
  description?: string;
  usage_type?: 'employee' | 'customer';
  default_operator_employee_id?: number;
  service_id?: number; // 关联的 AI Platform 服务 ID
  model_name?: string;
  max_tokens?: number;
  temperature?: number;
  system_prompt?: string;
  processing_mode?: 'sync' | 'async';
  timeout_seconds?: number;
  max_retries?: number;
  retry_delay_seconds?: number;
  is_active?: boolean;
}

export interface AgentQuery {
  tenant_code: string;
  agent_type?: 'chatbot' | 'assistant' | 'workflow';
  is_active?: boolean;
  service_id?: number;
  limit?: number;
  offset?: number;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  total?: number;
  message?: string;
  error?: string;
}

// ==================== API 客户端 ====================

class AgentAPIClient {
  private baseURL: string;

  constructor() {
    this.baseURL = API_BASE_AGENTS;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        headers: {
          'Content-Type': 'application/json',
          'x-tenant-code': localStorage.getItem('currentTenant') || 'default',
          ...options.headers,
        },
        ...options,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error: unknown) {
      console.error('Agent API request failed:', error);
      throw error;
    }
  }

  // ==================== Agents API ====================

  async getAgents(query: AgentQuery): Promise<ApiResponse<Agent[]>> {
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      // 排除 tenant_code，因为它已经在 URL 路径中
      if (key !== 'tenant_code' && value !== undefined && value !== null) {
        params.append(key, String(value));
      }
    });

    const queryString = params.toString();
    const url = queryString ? `/${query.tenant_code}/agents?${queryString}` : `/${query.tenant_code}/agents`;
    return this.request<Agent[]>(url);
  }

  async createAgent(request: CreateAgentRequest): Promise<ApiResponse<{ id: number }>> {
    // 从请求体中移除 tenant_code，因为它已经在 URL 路径中
    const { tenant_code, ...requestBody } = request;
    return this.request<{ id: number }>(`/${tenant_code}/agents`, {
      method: 'POST',
      body: JSON.stringify(requestBody),
    });
  }

  async updateAgent(id: number, request: UpdateAgentRequest): Promise<ApiResponse<void>> {
    const tenantCode = localStorage.getItem('currentTenant') || 'default';
    return this.request<void>(`/${tenantCode}/agents/${id}`, {
      method: 'PUT',
      body: JSON.stringify(request),
    });
  }

  async deleteAgent(id: number): Promise<ApiResponse<void>> {
    const tenantCode = localStorage.getItem('currentTenant') || 'default';
    return this.request<void>(`/${tenantCode}/agents/${id}`, {
      method: 'DELETE',
    });
  }

  // ==================== AI Platform 服务 ====================

  /**
   * 获取可用的 AI Platform 服务列表
   */
  async getAvailableAIPlatformServices(): Promise<ApiResponse<any[]>> {
    const tenantCode = localStorage.getItem('currentTenant') || 'default';
    return this.request<any[]>(`/${tenantCode}/agents/available-services`);
  }
}

export const agentService = new AgentAPIClient();
